import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
} from '@mui/material';
import type React from 'react';

import { Text, api, isDefined, isTruthy } from 'common';

import { CallerPhoneNumberFormField } from './CallerPhoneNumberFormField';
import { MODE_HELPER_TEXT, TIMEOUT_HELPER_TEXT } from './documentation';
import { BlockTextArrayField } from '../../ConfigFields/CodeMirror/BlockTextArray';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../ConfigFields/Duration';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';
import { SharedFlowArguments } from '../SubFlow/Shared/FlowArguments/SharedFlowArguments';
import { FlowVersionFormFields } from '../SubFlow/Shared/FlowVersion';
import { FlowKind } from '../constants';

const { ConversationBlockMode, DurationUnits } = api.FlowDefinition;

interface LegacyModeFormFieldsProps {
  existingConversationId?: string;
  patientReplyHandlerEnabled: boolean;
  onPatientReplyHandlerChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export function LegacyModeFormFields(props: LegacyModeFormFieldsProps) {
  const { existingConversationId, patientReplyHandlerEnabled, onPatientReplyHandlerChange } = props;

  const { value: callerPhoneNumberId } = useSelectedNodeField<string | null>(
    'config.callerPhoneNumberId'
  );
  const { value: agentPhoneNumber } = useSelectedNodeField<string | undefined>(
    'config.agentPhoneNumber'
  );
  const isCustomPhoneNumber = !isDefined(callerPhoneNumberId) && isDefined(agentPhoneNumber);

  return (
    <Stack gap={2}>
      <BlockValueTemplateField
        fieldName="config.existingConversationId"
        label="Existing Conversation ID"
        helperText="Provide an existing conversation ID to continue the conversation. When enabled, you will only be able to provide the message and a new timeout."
      />
      {!isTruthy(existingConversationId) && (
        <>
          <CallerPhoneNumberFormField />
          {isCustomPhoneNumber && (
            <BlockValueTemplateField
              fieldName="config.agentPhoneNumber"
              label="Custom Sender Phone Number"
              helperText="Provide a valid phone number starting with country code (e.g. +1 for US)"
              placeholder="+1-###-###-####"
            />
          )}
          <BlockValueTemplateField
            fieldName="config.priority"
            label="Priority"
            helperText="Provide a priority for the conversation"
            placeholder="24 hour"
          />
          <BlockTextArrayField
            label="Tags"
            addRowLabel="Add Tag"
            fieldName="config.tags"
            placeholder="Enter tag"
          />
          <Divider sx={{ marginY: 1.5 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={patientReplyHandlerEnabled}
                onChange={onPatientReplyHandlerChange}
              />
            }
            label="Add patient reply handler flow"
          />
          {patientReplyHandlerEnabled && (
            <FlowVersionFormFields
              fieldNamePrefix="config.patientReplyHandler"
              variant="SubflowPicker"
              allowedFlowKinds={[FlowKind.Base]}
            >
              <Accordion defaultExpanded variant="elevation">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text variant="heading5">Inputs</Text>
                </AccordionSummary>
                <AccordionDetails>
                  <SharedFlowArguments fieldName="config.patientReplyHandler.metadata" />
                </AccordionDetails>
              </Accordion>
            </FlowVersionFormFields>
          )}
          <Divider sx={{ marginY: 1.5 }} />
        </>
      )}
      <BlockValueTemplateField fieldName="config.firstMessage" label="Message" />
      <Text variant="subtitle2" bold>
        ⚠️ HIPAA Security Notice
      </Text>
      <Text variant="subtitle2">
        SMS communications are not encrypted or secure. DO NOT configure workflows that request,
        collect, or transmit PHI via SMS. <br></br> PHI includes: patient names, medical conditions,
        symptoms, medications, appointment details with providers, or any health-related
        information.
      </Text>
      <BlockSelectField
        fieldName="config.mode"
        label="Wait for conversation to close"
        helperText={MODE_HELPER_TEXT}
        options={[
          { value: ConversationBlockMode.WaitUntilClosed, label: 'Yes' },
          { value: ConversationBlockMode.NonBlocking, label: 'No' },
        ]}
      />
      <BlockDurationField
        variant="BlockValueTemplate"
        fieldName="config.timeout"
        unitFieldNameSuffix="units"
        required={true}
        allowedUnits={[DurationUnits.Minutes, DurationUnits.Hours, DurationUnits.Days]}
        toggleLabel="Timeout"
        valueLabel="Timeout"
        valueHelperText={TIMEOUT_HELPER_TEXT}
      />
    </Stack>
  );
}
